// src/hr/timeoff/TimeOffApp.js -- the employee-facing Time Off app, mounted at /time-off/*.
// Routes are relative: '' (My Time Off), whos-out, policies, team (only for managers), approvals
// (only for approvers). GET /api/hr/me decides what renders; the server enforces every rule regardless.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useResolvedPath } from 'react-router-dom';
import { FiAlertCircle, FiCheckSquare, FiClock, FiFileText, FiTool, FiUserCheck, FiUserX, FiUsers } from 'react-icons/fi';
import { hr } from '../api';
import { HrShell } from '../HrShell';
import useHrMe from '../useHrMe';
import { Btn, EmptyState, Skeleton, Toast, useToast } from '../kit';
import { todayNY } from './dates';
import MyTimeOff from './MyTimeOff';
import WhosOutPage from './WhosOutPage';
import PoliciesPage from './PoliciesPage';
import ApprovalsPage from './ApprovalsPage';
import MyTeamPage from './MyTeamPage';

const CRUMBS = { '': 'My Time Off', 'whos-out': "Who's Out", policies: 'Policies', team: 'My team', approvals: 'Approvals' };

// Requests waiting on this approver, loaded once for the menu badge and the Approvals page.
// reload() keeps the current list on screen until the new one arrives (and keeps it if the reload
// fails), and resolves to { status, data } so a caller can react to the fresh list.
function useApprovals(enabled) {
  const [state, setState] = useState({ status: 'idle', data: null });
  const latest = useRef(0);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    latest.current += 1;
    const call = latest.current;
    setState((s) => ({ ...s, status: 'loading' }));
    let next;
    try {
      const data = await hr.approvals();
      next = { status: 'ready', data: data || { requests: [], decided: [] } };
    } catch (err) {
      next = { status: 'error' };
    }
    if (mounted.current && call === latest.current) setState((s) => ({ ...s, ...next }));
    return next;
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (enabled) load();
    return () => { mounted.current = false; };
  }, [enabled, load]);

  return { ...state, reload: load };
}

function LoadingPage() {
  return (
    <div className="hrp-page hrp-loading">
      <Skeleton label="Loading your time off" width={260} height={26} />
      <Skeleton width={340} height={12} className="hrp-loading__sub" />
      <div className="hrp-to-top">
        <Skeleton variant="card" />
        <div className="hrp-side">
          <Skeleton variant="block" height={98} />
          <Skeleton variant="block" height={98} />
        </div>
      </div>
      <div className="hrp-to-low">
        <Skeleton variant="block" height={240} />
        <Skeleton variant="block" height={240} />
      </div>
    </div>
  );
}

const ME_ITEMS = [
  { to: '.', label: 'My Time Off', icon: FiClock },
  { to: 'whos-out', label: "Who's Out", icon: FiUsers },
  { to: 'policies', label: 'Policies', icon: FiFileText }
];

// today: optional 'YYYY-MM-DD' override (tests); defaults to today in New York, like the server.
export function TimeOffApp({ today: todayProp }) {
  const today = todayProp || todayNY();
  const { status, me, reload } = useHrMe();
  const [toast, showToast, dismissToast] = useToast();
  const location = useLocation();
  const base = useResolvedPath('.').pathname.replace(/\/+$/, '');

  const ready = status === 'ready' && !!me && !!me.employee;
  const isApprover = ready && !!me.isApprover;
  const isManager = ready && !!me.isManager;
  const approvals = useApprovals(isApprover);

  const page = location.pathname.slice(base.length).replace(/^\/+/, '').split('/')[0];
  const crumb = CRUMBS[page] || CRUMBS[''];

  const sections = [];
  if (status === 'loading' || ready) {
    const pending = ready ? (me.pendingPolicies || []).length : 0;
    sections.push({
      title: 'Me',
      items: ME_ITEMS.map((item) => (item.to === 'policies' ? { ...item, badge: pending || undefined } : item))
    });
  }
  // "My team": the manager's own team page, then Approvals for approvers (each shown on its own rule).
  const teamItems = [];
  if (isManager) teamItems.push({ to: 'team', label: 'My team', icon: FiUserCheck });
  if (isApprover) {
    const waiting = approvals.data ? (approvals.data.requests || []).length : 0;
    teamItems.push({ to: 'approvals', label: 'Approvals', icon: FiCheckSquare, badge: waiting || undefined });
  }
  if (teamItems.length) sections.push({ title: 'My team', items: teamItems });

  const person = ready
    ? {
      id: me.employee.id,
      name: [me.employee.firstName, me.employee.lastName].filter(Boolean).join(' '),
      sub: me.employee.department || me.employee.title || ''
    }
    : null;

  let body;
  if (status === 'loading') {
    body = <LoadingPage />;
  } else if (status === 'unavailable') {
    body = (
      <EmptyState icon={FiTool} title="Time Off is being set up — check back soon.">
        Your balances, requests and who's out will appear here.
      </EmptyState>
    );
  } else if (status === 'error' || !me) {
    body = (
      <EmptyState icon={FiAlertCircle} title="We couldn't load your time off" action={<Btn variant="ghost" onClick={reload}>Try again</Btn>}>
        Something went wrong on our side. Try again in a moment.
      </EmptyState>
    );
  } else if (!me.employee) {
    body = (
      <EmptyState icon={FiUserX} title="You're not set up for Time Off yet. Contact HR.">
        HR adds everyone to Time Off. Once you're on the list, this page shows your balances.
      </EmptyState>
    );
  } else {
    body = (
      <Routes>
        <Route index element={<MyTimeOff me={me} today={today} reload={reload} onToast={showToast} />} />
        <Route path="whos-out" element={<WhosOutPage today={today} />} />
        <Route path="policies" element={<PoliciesPage today={today} onToast={showToast} onChanged={reload} />} />
        <Route path="team" element={isManager ? <MyTeamPage today={today} /> : <Navigate to=".." replace />} />
        <Route
          path="approvals"
          element={isApprover
            ? <ApprovalsPage approvals={approvals} types={me.types} onToast={showToast} />
            : <Navigate to=".." replace />}
        />
        <Route path="*" element={<Navigate to=".." replace />} />
      </Routes>
    );
  }

  return (
    <HrShell brand="Time Off" brandLetter="T" sections={sections} person={person} crumb={crumb}>
      {body}
      <Toast toast={toast} onDismiss={dismissToast} />
    </HrShell>
  );
}

export default TimeOffApp;
